/* eslint-disable object-property-newline */
import React, { useContext } from 'react';
import ReactDOM from 'react-dom';
import { ErrorBoundary } from '@leverege/ui-elements';
import { ToolbarContext } from '@leverege/ui-dashboard';
import FilterSourceModel from "../filters/FilterSourceModel.js";
import FilterSourcesModel from "../filters/FilterSourcesModel.js";
import DataViewerModel from "./DataViewerModel.js";
import DataViewerRenderer from "./DataViewerRenderer.js";
import { jsx as _jsx } from "react/jsx-runtime";
const EMPTY_FILTER_SOURCE = Object.freeze(FilterSourceModel.create());

/**
 * Returns the props for a DataViewer
 * @param {DataViewerModel} dv The model representing th data viwer. { id, type, settings }
 * @param {Object} common the props for the data viewer's renderer/component
 * @param {Object} opts object containing data viewer options like settingsPath, settings, 
 * settingsData, filters, overrides and overridesData
 * @param  {...any} rest other objects to add to the props
 * @returns 
 */
function dvProps(dvm, common, ...rest) {
  const {
    settingsPath,
    overrides,
    filters
  } = common;
  const id = dvm.id || dvm.type;
  let rtn = {
    ...common,
    dataViewerId: id,
    dataViewerModel: dvm,
    settingsPath: DataViewerModel.getSettingsPath(dvm, settingsPath),
    overrides,
    overrideModel: overrides?.get(id)
  };
  if (filters) {
    rtn.filterName = dvm.id || dvm.type;
    rtn.filter = FilterSourcesModel.getSource(filters, rtn.filterName) || EMPTY_FILTER_SOURCE;
  }
  rest.forEach(m => {
    if (typeof m === 'object') {
      Object.assign(rtn, m);
    } else if (typeof m === 'function') {
      rtn = m(dvm, rtn) || rtn;
    }
  });
  return rtn;
}

/**
   * Invoked when the error boundary detects an error and the user
   * presses the reset button. If the DataViewer has a reset function,
   * it will be invoked
   */
function onErrorReset(evt) {
  const {
    data: {
      dataViewer,
      props
    }
  } = evt;
  if (typeof dataViewer?.reset === 'function') {
    return dataViewer.reset(props);
  }
  return null;
}

/**
 * Render DataViwerModel. A convenience for adding a DataViewer and error boundary.
 */
function rdvm(dataViewerModel, common, ...rest) {
  if (dataViewerModel == null) {
    return null;
  }
  const dataViewer = common?.dataViewers?.getByType(dataViewerModel);
  if (dataViewer == null) {
    return null;
  }
  const props = dvProps(dataViewerModel, common, ...rest);
  props.settings = dataViewerModel.settings;
  return render(dataViewer, props);
}
function render(dataViewer, props) {
  const ref = /*#__PURE__*/React.createRef();
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    ref: ref,
    eventData: {
      props,
      dataViewer,
      ref
    },
    onReset: onErrorReset,
    children: /*#__PURE__*/_jsx(DataViewerRenderer, {
      ...props,
      dataViewer: dataViewer
    })
  }, `${dataViewer.type}-error-boundary`);
}

/**
 * RenderHiddenDataViwer. A convenience for adding a Hidden ataViewer and error boundary.
 */
function rhdv(dataViewer, common, ...rest) {
  if (dataViewer == null) {
    return null;
  }
  const dataViewerModel = {
    type: dataViewer.type,
    id: dataViewer.type
  };
  const props = dvProps(dataViewerModel, common, ...rest);
  return render(dataViewer, props);
}
function HeaderToolbar({
  inCard,
  content
}) {
  const toolbarRef = useContext(ToolbarContext);
  if (inCard && toolbarRef) {
    return /*#__PURE__*/ReactDOM.createPortal(content, toolbarRef);
  }
  return content;
}
export { rdvm, rhdv, dvProps, onErrorReset, HeaderToolbar, EMPTY_FILTER_SOURCE };
export default {
  rdvm,
  rhdv,
  dvProps,
  onErrorReset,
  HeaderToolbar,
  EMPTY_FILTER_SOURCE
};